import type { Ref } from 'react'

import { cn } from '@moment/design-system-ui/lib/cn'
import { cva, type VariantProps } from 'class-variance-authority'

import { LoadingSpinner } from '#components/_primitives/loading-spinner'
import { Text } from '#components/controls/text'

import { ButtonVariant } from './variants'

export { ButtonVariant } from './variants'

const buttonVariants = cva(
  // Base styles - applied to all variants
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 h-10 rounded-[0.625rem] transition-colors duration-250 ease outline-offset-3 w-full overflow-hidden',
  {
    variants: {
      variant: {
        [ButtonVariant.Primary]: cn(
          // Background colors
          'bg-(--color-surface-accent) dark:bg-(--color-surface-secondary)',

          // Text colors
          'text-(--color-text-primary-contrast) dark:text-(--color-text-primary)',

          // Hover states
          'hover:bg-(--color-surface-secondary-contrast) dark:hover:bg-(--color-surface-secondary)/89',

          // Focus states
          'focus-within:bg-(--color-surface-secondary-contrast) dark:focus-within:bg-(--color-surface-secondary)/85',

          // Shadow effects
          'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.03)] dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)]',
        ),

        [ButtonVariant.Secondary]: cn(
          // Background colors
          'bg-(--color-surface-primary) dark:bg-(--color-surface-secondary)/4',

          // Text colors
          'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',

          // Border styles
          'border border-(--color-border-primary) dark:border-(--color-border-primary-contrast)/40',

          // Hover states
          'hover:bg-(--color-surface-secondary)/30 dark:hover:bg-(--color-surface-secondary)/3',

          // Focus states
          'focus-within:bg-(--color-surface-secondary)/30 focus-within:bg-(--color-surface-secondary)/3',

          // Shadow effects
          'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.03)] dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)]',
        ),
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      {
        variant: ButtonVariant.Primary,
        disabled: true,
        class: cn(
          // Opacity adjustments (disabled state)
          'opacity-90 hover:opacity-90 dark:opacity-100 dark:hover:opacity-100',

          // Background colors (disabled state)
          'bg-(--color-surface-secondary-contrast) hover:bg-(--color-surface-secondary-contrast) dark:bg-(--color-surface-secondary) dark:hover:bg-(--color-surface-secondary)',

          // Shadow effects (disabled state)
          'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.02)] dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]',
        ),
      },
      {
        variant: ButtonVariant.Secondary,
        disabled: true,
        class: cn(
          // Background colors (disabled state)
          'bg-(--color-surface-secondary) dark:bg-(--color-surface-secondary)/2 hover:bg-(--color-surface-secondary)',

          // Border styles (disabled state)
          'dark:border-(--color-border-secondary-contrast)',

          // Text colors (disabled state)
          'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',

          // Shadow effects (disabled state)
          'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.02)] dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]',
        ),
      },
    ],
    defaultVariants: {
      variant: ButtonVariant.Primary,
      disabled: false,
    },
  },
)

type ButtonCvaVariant =
  | typeof ButtonVariant.Primary
  | typeof ButtonVariant.Secondary

interface BaseButtonProps extends VariantProps<typeof buttonVariants> {
  label: string
  isLoading?: boolean
  loadingText?: string
  className?: string
  variant?: ButtonCvaVariant
}

interface ButtonAsButton extends BaseButtonProps {
  as?: 'button'
  type?: 'button' | 'submit' | 'reset'
  onClick?: () => void
  disabled?: boolean
  ref?: Ref<HTMLButtonElement>
  'data-testid'?: string
}

interface ButtonAsLink extends BaseButtonProps {
  as: 'a'
  href: string
  target?: string
  rel?: string
  disabled?: never
  ref?: Ref<HTMLAnchorElement>
}

type ButtonProps = ButtonAsButton | ButtonAsLink

export const Button = ({
  label,
  variant = ButtonVariant.Primary,
  disabled = false,
  isLoading = false,
  loadingText,
  className,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || isLoading

  const spinnerDarkVariant =
    variant === ButtonVariant.Secondary
      ? ButtonVariant.Secondary
      : ButtonVariant.Ghost

  const labelText = isLoading ? loadingText : label

  const content = (
    <>
      {isLoading && (
        <>
          <LoadingSpinner className="flex dark:hidden" variant={variant} />
          <LoadingSpinner
            className="hidden dark:flex"
            variant={spinnerDarkVariant}
          />
        </>
      )}
      {labelText && (
        <Text as="span" variant="base">
          {labelText}
        </Text>
      )}
    </>
  )

  const baseClasses = cn(
    buttonVariants({ variant, disabled: isDisabled }),
    className,
  )

  const wrapperClasses =
    'has-[:active]:scale-99 w-full transition-[scale] duration-100 ease'

  if (props.as === 'a') {
    const { href, target, rel, ref } = props
    return (
      <div className={isDisabled ? '' : wrapperClasses}>
        <a
          ref={ref}
          className={baseClasses}
          href={href}
          target={target}
          rel={rel}
        >
          {content}
        </a>
      </div>
    )
  }

  const { onClick, type, ref } = props
  const testId = props['data-testid']
  return (
    <div className={isDisabled ? '' : wrapperClasses}>
      <button
        ref={ref}
        type={type ?? 'button'}
        disabled={isDisabled}
        className={baseClasses}
        onClick={onClick}
        data-testid={testId}
      >
        {content}
      </button>
    </div>
  )
}
